import {
  change,
  setSubmitSucceeded,
  startSubmit,
  stopSubmit,
} from "redux-form";
import { all, call, fork, put, select, take } from "redux-saga/effects";

import { NotificationClient } from "../../config/view_const/notification";
import { AppError, ErrorType } from "../../domain/model/app_error";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import { Contract } from "../../domain/model/contract";
import { Customer } from "../../domain/model/customer";
import { FrontDate } from "../../domain/model/frontDate";
import { Project } from "../../domain/model/project";
import { User } from "../../domain/model/user";
import { IContractRepository } from "../../domain/repository/contract_repository";
import { ICustomerRepository } from "../../domain/repository/customer_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";
import {
  createFileUsecase,
  IFileUseCase,
} from "../../domain/usecase/file_usecase";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import { createCustomerAPI } from "../../infra/api/service/customer_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
  ClientContractActionType,
  createClientContractDetailActionCreator,
  IClientContractActionCreator,
  IRequestApproveContractAction,
  IRequestOpenCreateFormContractAction,
  IRequestUpdateContractAction,
} from "../action/client_contract_action";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import { OpenCreateFormContractByClientRequest } from "../request/contract_request";
import {
  OpenCreateFormContractByClientResponse,
  UpdateContractByClientResponse,
} from "../response/contract_response";
import { AppState } from "../store/app_state";
import { AuthState } from "../store/auth_state";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: IClientContractActionCreator =
  createClientContractDetailActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

function* handleOpenCreateFormContractInClientContract() {
  while (true) {
    try {
      const action: IRequestOpenCreateFormContractAction = yield take(
        ClientContractActionType.REQUEST_OPENCREATEFORM_CONTRACT
      );

      let contract: Contract | null = null;

      try {
        contract = yield call(
          getContract,
          action.item.companyId,
          action.item.projectId,
          action.item.customerId
        );
      } catch (error) {
        const e = error as AppError;
        if (!(e.type === ErrorType.API_REQUEST_404)) {
          throw error;
        }
      }

      if (!contract) {
        //    データが存在しない場合は新規にinitialValue用にcontractを作る
        const projectId: string = action.item.projectId;
        const customerId: string = action.item.customerId;
        let project: Project;
        let customer: Customer;
        [project, customer] = yield all([
          call(getProject, projectId),
          call(getCustomer, customerId),
        ]);

        const newContract: Contract = new Contract(
          "create",
          projectId,
          customerId,
          "",
          "",
          "",
          "",
          0,
          entity.Contract.Interval.Interval_Unknown,
          0,
          0,
          entity.Contract.Interval.Interval_Unknown,
          0,
          entity.Contract.Type.Type_Unknown,
          "",
          "",
          entity.Contract.Requester.Requester_Unknown,
          false,
          FrontDate.fromEmpty(),
          FrontDate.fromEmpty()
        );

        const res: OpenCreateFormContractByClientResponse =
          new OpenCreateFormContractByClientResponse();
        res.project = project;
        res.customer = customer;
        res.contract = newContract;
        yield put(
          actionCreator.callbackOpenCreateFormContractAction(true, res)
        );
      } else {
        // データが存在する場合
        const projectId: string = action.item.projectId;
        const customerId: string = action.item.customerId;
        let project: Project;
        let customer: Customer;
        [project, customer] = yield all([
          call(getProject, projectId),
          call(getCustomer, customerId),
        ]);
        const res: OpenCreateFormContractByClientResponse =
          new OpenCreateFormContractByClientResponse();
        res.contract = contract;
        res.project = project;
        res.customer = customer;
        yield put(
          actionCreator.callbackOpenCreateFormContractAction(true, res)
        );
      }
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackOpenCreateFormContractAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleUpdateContractInClientContract() {
  while (true) {
    try {
      const action: IRequestUpdateContractAction = yield take(
        ClientContractActionType.REQUEST_UPDATE_CONTRACT
      );
      yield put(startSubmit("ClientContractMainEditForm"));
      const authSelector = (state: AppState) => state.authReducer;
      const _authState: AuthState = yield select(authSelector);
      const user: User | null = _authState.user;
      const fileUrl: string = action.item.uploadFile
        ? yield call(
            createContractFile,
            user,
            action.item.uploadFile,
            action.item.companyId,
            action.item.projectId,
            action.item.customerId
          )
        : "";
      const contract: Contract = yield call(
        updateContract,
        action.item.projectId,
        action.item.customerId,
        fileUrl
      );
      const res: UpdateContractByClientResponse =
        new UpdateContractByClientResponse();
      res.contract = contract;
      yield put(actionCreator.callbackCreateContractAction(true, res));
      // Upload後処理（FileObjectの削除）
      yield put(
        change("ClientContractMainEditForm", "fileURLFileObject", null)
      );
      yield put(stopSubmit("ClientContractMainEditForm"));
      yield put(setSubmitSucceeded("ClientContractMainEditForm"));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationClient.successUpdateContract;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackCreateContractAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleApproveContractInClientContract() {
  while (true) {
    try {
      const action: IRequestApproveContractAction = yield take(
        ClientContractActionType.REQUEST_APPROVE_CONTRACT
      );
      yield call(
        approveContract,
        action.item.projectId,
        action.item.customerId
      );
      yield put(actionCreator.callbackApproveContractAction(true));

      const req: OpenCreateFormContractByClientRequest =
        new OpenCreateFormContractByClientRequest();
      req.companyId = action.item.companyId;
      req.projectId = action.item.projectId;
      req.customerId = action.item.customerId;
      yield put(actionCreator.requestOpenCreateFormContractAction(req));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationClient.successApproveContract;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
      yield put(stopSubmit("ClientContractMainEditForm"));
      yield put(setSubmitSucceeded("ClientContractMainEditForm"));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackCreateContractAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

const getContract = (
  companyId: string,
  projectId: string,
  customerId: string
): Promise<Contract> => {
  return contractRepository.get(companyId, projectId, customerId);
};

const getProject = (projectId: string): Promise<Project> => {
  return projectRepository.get(projectId);
};

const getCustomer = (customerId: string): Promise<Customer> => {
  return customerRepository.get(customerId);
};

const createContractFile = (
  user: User,
  uploadFile: File,
  companyId: string,
  projectId: string,
  customerId: string
): Promise<string> => {
  return fileUseCase.createContractFile(
    user,
    uploadFile,
    companyId,
    projectId,
    customerId
  );
};

const updateContract = (
  projectId: string,
  customerId: string,
  fileUrl: string
): Promise<Contract> => {
  return contractRepository.update(projectId, customerId, fileUrl);
};

const approveContract = (
  projectId: string,
  customerId: string
): Promise<void> => {
  return contractRepository.approve(projectId, customerId);
};

export {
  handleUpdateContractInClientContract,
  handleOpenCreateFormContractInClientContract,
  handleApproveContractInClientContract,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_contract_handler.ts